import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { FlaskConical, LogOut, Calendar, Home as HomeIcon, MessageSquare, Star, Phone, MapPin, Check, Trash2, Clock, Briefcase, Building2, Download, Mail } from "lucide-react";

const StatCard = ({ icon: Icon, label, value }) => (
  <div className="bg-white rounded-2xl p-5 border border-slate-100 shadow-sm flex items-center gap-4" data-testid={`stat-card-${label}`}>
    <span className="w-12 h-12 rounded-xl bg-turq/10 flex items-center justify-center text-turq-dark"><Icon className="w-6 h-6" /></span>
    <div><p className="text-2xl font-bold text-navy font-heading">{value ?? 0}</p><p className="text-xs text-secondary">{label}</p></div>
  </div>
);

const fmt = (d) => d ? new Date(d).toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" }) : "";

export default function Admin() {
  const { user, logout, loading } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState({});
  const [appts, setAppts] = useState([]);
  const [homes, setHomes] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [reviews, setReviews] = useState([]);
  const [careers, setCareers] = useState([]);
  const [partnerships, setPartnerships] = useState([]);

  const loadAll = useCallback(async () => {
    try {
      const [s, a, h, c, r, cr, p] = await Promise.all([
        api.get("/admin/stats"), api.get("/admin/appointments"), api.get("/admin/home-collections"),
        api.get("/admin/contacts"), api.get("/admin/testimonials"),
        api.get("/admin/careers"), api.get("/admin/partnerships"),
      ]);
      setStats(s.data); setAppts(a.data); setHomes(h.data); setContacts(c.data); setReviews(r.data);
      setCareers(cr.data); setPartnerships(p.data);
    } catch { toast.error("Failed to load data"); }
  }, []);

  useEffect(() => {
    if (!loading && !user) { navigate("/login"); return; }
    if (user) loadAll();
  }, [user, loading, navigate, loadAll]);

  const approve = async (id) => { await api.put(`/admin/testimonials/${id}/approve`); toast.success("Approved"); loadAll(); };
  const remove = async (id) => { await api.delete(`/admin/testimonials/${id}`); toast.success("Deleted"); loadAll(); };

  const downloadCv = async (id, fname) => {
    try {
      const res = await api.get(`/admin/careers/${id}/cv`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = fname || "cv"; a.click();
      URL.revokeObjectURL(url);
    } catch { toast.error("Could not download CV"); }
  };

  if (loading || !user) return <div className="min-h-screen flex items-center justify-center text-secondary">Loading...</div>;

  return (
    <div className="min-h-screen bg-slate-50" data-testid="admin-page">
      <header className="glass-nav border-b border-slate-100 sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-5 md:px-8 h-16 flex items-center justify-between">
          <div className="flex items-center gap-2.5">
            <div className="w-9 h-9 rounded-xl bg-navy flex items-center justify-center"><FlaskConical className="w-5 h-5 text-turq-light" /></div>
            <span className="font-heading font-bold text-navy">DOABA Admin</span>
          </div>
          <Button variant="ghost" onClick={() => { logout(); navigate("/login"); }} className="text-navy" data-testid="admin-logout-btn"><LogOut className="w-4 h-4 mr-2" /> Logout</Button>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-5 md:px-8 py-8">
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 mb-8">
          <StatCard icon={Calendar} label="Appointments" value={stats.appointments} />
          <StatCard icon={HomeIcon} label="Home Collections" value={stats.home_collections} />
          <StatCard icon={MessageSquare} label="Messages" value={stats.contacts} />
          <StatCard icon={Briefcase} label="Applications" value={stats.careers} />
          <StatCard icon={Building2} label="Partnerships" value={stats.partnerships} />
          <StatCard icon={Star} label="Pending Reviews" value={stats.pending_testimonials} />
        </div>

        <Tabs defaultValue="appointments">
          <TabsList className="bg-white rounded-full p-1 border border-slate-100 mb-6 flex-wrap h-auto">
            <TabsTrigger value="appointments" className="rounded-full data-[state=active]:bg-navy data-[state=active]:text-white" data-testid="tab-appointments">Appointments</TabsTrigger>
            <TabsTrigger value="home" className="rounded-full data-[state=active]:bg-navy data-[state=active]:text-white" data-testid="tab-home">Home Collection</TabsTrigger>
            <TabsTrigger value="contacts" className="rounded-full data-[state=active]:bg-navy data-[state=active]:text-white" data-testid="tab-contacts">Messages</TabsTrigger>
            <TabsTrigger value="careers" className="rounded-full data-[state=active]:bg-navy data-[state=active]:text-white" data-testid="tab-careers">Applications</TabsTrigger>
            <TabsTrigger value="partnerships" className="rounded-full data-[state=active]:bg-navy data-[state=active]:text-white" data-testid="tab-partnerships">Partnerships</TabsTrigger>
            <TabsTrigger value="reviews" className="rounded-full data-[state=active]:bg-navy data-[state=active]:text-white" data-testid="tab-reviews">Reviews</TabsTrigger>
          </TabsList>

          <TabsContent value="appointments">
            <div className="space-y-3" data-testid="appointments-list">
              {appts.length === 0 && <p className="text-secondary">No appointments yet.</p>}
              {appts.map((a) => (
                <div key={a.id} className="bg-white rounded-2xl p-5 border border-slate-100 flex flex-wrap gap-4 justify-between" data-testid={`appt-${a.id}`}>
                  <div><p className="font-semibold text-navy">{a.name}</p><p className="text-sm text-secondary flex items-center gap-1"><Phone className="w-3.5 h-3.5" /> {a.phone}</p>{a.test && <Badge className="mt-2 bg-turq/10 text-turq-dark hover:bg-turq/10">{a.test}</Badge>}</div>
                  <div className="text-right text-sm text-secondary"><p className="flex items-center gap-1 justify-end"><Calendar className="w-3.5 h-3.5" /> {a.date} {a.time}</p><p className="flex items-center gap-1 justify-end mt-1 text-xs text-muted"><Clock className="w-3 h-3" /> {fmt(a.created_at)}</p></div>
                </div>
              ))}
            </div>
          </TabsContent>

          <TabsContent value="home">
            <div className="space-y-3" data-testid="home-list">
              {homes.length === 0 && <p className="text-secondary">No home collection requests yet.</p>}
              {homes.map((h) => (
                <div key={h.id} className="bg-white rounded-2xl p-5 border border-slate-100" data-testid={`home-${h.id}`}>
                  <div className="flex flex-wrap gap-4 justify-between">
                    <div><p className="font-semibold text-navy">{h.name}</p><p className="text-sm text-secondary flex items-center gap-1"><Phone className="w-3.5 h-3.5" /> {h.phone}</p></div>
                    <p className="text-xs text-muted">{fmt(h.created_at)}</p>
                  </div>
                  {h.address && <p className="text-sm text-secondary flex items-start gap-1 mt-2"><MapPin className="w-3.5 h-3.5 mt-0.5" /> {h.address}</p>}
                  {h.test && <Badge className="mt-2 bg-turq/10 text-turq-dark hover:bg-turq/10">{h.test}</Badge>}
                </div>
              ))}
            </div>
          </TabsContent>

          <TabsContent value="contacts">
            <div className="space-y-3" data-testid="contacts-list">
              {contacts.length === 0 && <p className="text-secondary">No messages yet.</p>}
              {contacts.map((c) => (
                <div key={c.id} className="bg-white rounded-2xl p-5 border border-slate-100" data-testid={`contact-${c.id}`}>
                  <div className="flex flex-wrap gap-4 justify-between">
                    <div><p className="font-semibold text-navy">{c.name}</p><p className="text-sm text-secondary">{c.phone} {c.email && `• ${c.email}`}</p></div>
                    <p className="text-xs text-muted">{fmt(c.created_at)}</p>
                  </div>
                  <p className="text-sm text-secondary mt-2">{c.message}</p>
                </div>
              ))}
            </div>
          </TabsContent>

          <TabsContent value="careers">
            <div className="space-y-3" data-testid="careers-list">
              {careers.length === 0 && <p className="text-secondary">No job applications yet.</p>}
              {careers.map((c) => (
                <div key={c.id} className="bg-white rounded-2xl p-5 border border-slate-100" data-testid={`career-${c.id}`}>
                  <div className="flex flex-wrap gap-4 justify-between items-start">
                    <div>
                      <p className="font-semibold text-navy">{c.name}</p>
                      <p className="text-sm text-secondary flex items-center gap-1"><Phone className="w-3.5 h-3.5" /> {c.phone} {c.email && <span className="flex items-center gap-1 ml-2"><Mail className="w-3.5 h-3.5" /> {c.email}</span>}</p>
                      <Badge className="mt-2 bg-turq/10 text-turq-dark hover:bg-turq/10">{c.position}</Badge>
                    </div>
                    <div className="text-right">
                      <p className="text-xs text-muted">{fmt(c.created_at)}</p>
                      {c.cv_path && <Button size="sm" onClick={() => downloadCv(c.id, c.cv_name)} className="mt-2 rounded-full bg-navy hover:bg-navy-light" data-testid={`cv-download-${c.id}`}><Download className="w-4 h-4 mr-1" /> {c.cv_name || "Download CV"}</Button>}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </TabsContent>

          <TabsContent value="partnerships">
            <div className="space-y-3" data-testid="partnerships-list">
              {partnerships.length === 0 && <p className="text-secondary">No partnership enquiries yet.</p>}
              {partnerships.map((p) => (
                <div key={p.id} className="bg-white rounded-2xl p-5 border border-slate-100" data-testid={`partnership-${p.id}`}>
                  <div className="flex flex-wrap gap-4 justify-between">
                    <div><p className="font-semibold text-navy flex items-center gap-2"><Building2 className="w-4 h-4 text-turq-dark" /> {p.organization}</p><p className="text-sm text-secondary mt-1">{p.contact_person} • {p.phone} {p.email && `• ${p.email}`}</p></div>
                    <p className="text-xs text-muted">{fmt(p.created_at)}</p>
                  </div>
                  {p.message && <p className="text-sm text-secondary mt-2">{p.message}</p>}
                </div>
              ))}
            </div>
          </TabsContent>

          <TabsContent value="reviews">
            <div className="space-y-3" data-testid="reviews-list">
              {reviews.length === 0 && <p className="text-secondary">No reviews yet.</p>}
              {reviews.map((r) => (
                <div key={r.id} className="bg-white rounded-2xl p-5 border border-slate-100" data-testid={`review-${r.id}`}>
                  <div className="flex flex-wrap gap-4 justify-between items-start">
                    <div>
                      <p className="font-semibold text-navy">{r.name} {r.location && <span className="text-xs text-muted font-normal">• {r.location}</span>}</p>
                      <div className="flex text-amber-400 mt-1">{[...Array(r.rating || 5)].map((_, i) => <Star key={i} className="w-3.5 h-3.5 fill-current" />)}</div>
                    </div>
                    {r.approved ? <Badge className="bg-emerald-100 text-emerald-700 hover:bg-emerald-100">Approved</Badge> : <Badge className="bg-amber-100 text-amber-700 hover:bg-amber-100">Pending</Badge>}
                  </div>
                  <p className="text-sm text-secondary mt-2">{r.message}</p>
                  <div className="flex gap-2 mt-3">
                    {!r.approved && <Button size="sm" onClick={() => approve(r.id)} className="rounded-full bg-emerald-500 hover:bg-emerald-600" data-testid={`approve-${r.id}`}><Check className="w-4 h-4 mr-1" /> Approve</Button>}
                    <Button size="sm" variant="outline" onClick={() => remove(r.id)} className="rounded-full text-red-500 border-red-200 hover:bg-red-50" data-testid={`delete-${r.id}`}><Trash2 className="w-4 h-4 mr-1" /> Delete</Button>
                  </div>
                </div>
              ))}
            </div>
          </TabsContent>
        </Tabs>
      </main>
    </div>
  );
}
